/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { FunctionComponent } from "react";

import { StrapiBlogExtended } from "@/types/strapi";

import { spacingSets } from "@/tokens/configs/spacing_config";
import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Picture } from "@/ui/atoms/picture";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { Text } from "@/ui/atoms/text";

import { pluralizeAuthorList } from "@/util/blog_util";
import { formatDate } from "@/util/date_util";
import { buildStylesByBreakpoint } from "@/util/style_util";

const BlogArticleHero: FunctionComponent<StrapiBlogExtended> = (props) => {
    /**
     * Styles
     */
    const heroContainerStyles = buildStylesByBreakpoint(
        "rowGap",
        spacingSets.BlogArticleHeroImageMargin,
    );

    const articleMetaContainerStyles = css({
        columnGap: Spacing["spacing-2"],
        display: "flex",
        flexDirection: "column",
        rowGap: Spacing["spacing-half"],
    });

    /**
     * Elements
     */
    return (
        <GridContainer
            className={heroContainerStyles}
            marginTop="BlogArticleTitleMargin"
        >
            <SubgridContainer
                className={css({
                    alignItems: "flex-end",
                    rowGap: Spacing["spacing-3"],
                })}
            >
                <Text
                    columnSpan={{
                        extraSmall: "full",
                        medium: 9,
                        large: 8,
                    }}
                    columnStart={1}
                    fontSize="ArticleHeadline"
                    tag="h1"
                >
                    {props.Title}
                </Text>

                <GridColumn
                    columnSpan={{
                        extraSmall: "full",
                        medium: 3,
                        large: 3,
                    }}
                    columnStart={{ extraSmall: 1, medium: 10, large: 10 }}
                >
                    <div css={articleMetaContainerStyles}>
                        <Text
                            fontSize="Attribution"
                            shouldBalanceWrap={true}
                            textAlign={{
                                extraSmall: "left",
                                medium: "right",
                            }}
                        >
                            {pluralizeAuthorList(props.Authors)}
                        </Text>

                        <Text
                            dateTime={props.Publish_Date}
                            fontSize="Attribution"
                            tag="time"
                            textAlign={{
                                extraSmall: "left",
                                medium: "right",
                            }}
                            themeKey="textSecondary"
                        >
                            {formatDate(props.Publish_Date)}
                        </Text>
                    </div>
                </GridColumn>
            </SubgridContainer>

            <GridColumn>
                <Picture
                    {...props.Hero_Media}
                    alternativeText={props.Title}
                    priority={true}
                    quality={90}
                    sizeConfiguration="fullWidth"
                />
            </GridColumn>
        </GridContainer>
    );
};

export default BlogArticleHero;
